import React, { useEffect, useState, useRef } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  Box,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Slide
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Seuil pour détecter l'ouverture du clavier virtuel (en pixels)
const KEYBOARD_THRESHOLD = 150;

// Transition pour les dialogs sur mobile
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const BaseDialog = ({
  open,
  onClose,
  title,
  subtitle,
  icon,
  children,
  actions,
  maxWidth = 'md',
  fullWidth = true,
  fullScreen,
  disableClose = false,
  showCloseButton = true,
  contentProps = {},
  headerProps = {},
  paperProps = {},
  useTransition = true,
  customPaddingContent = null,
  customBackgroundContent = '#fafbff',
  ...dialogProps
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [dialogHeight, setDialogHeight] = useState('100dvh');
  const lastValidHeight = useRef('100dvh'); // Conserver la dernière hauteur valide

  // Gestion du visualViewport pour mobile
  useEffect(() => {
    if (!isMobile) return;

    const updateHeight = () => {
      if (!window.visualViewport) {
        setDialogHeight('100dvh'); // fallback pour les navigateurs qui ne gèrent pas visualViewport
        return;
      }
      
      const viewportHeight = window.visualViewport.height;
      const isKeyboard = window.innerHeight - viewportHeight > KEYBOARD_THRESHOLD;
      
      if (!isKeyboard) {
        // Pas de clavier détecté : mettre à jour normalement
        lastValidHeight.current = `${viewportHeight}px`;
        setDialogHeight(lastValidHeight.current);
      } else {
        // Clavier détecté : conserver la dernière hauteur valide
        setDialogHeight(lastValidHeight.current);
      }
    };

    updateHeight(); // ajustement initial
    window.visualViewport?.addEventListener('resize', updateHeight);
    
    return () => {
      window.visualViewport?.removeEventListener('resize', updateHeight);
    };
  }, [isMobile]);

  const handleClose = (event, reason) => {
    if (disableClose && reason === 'backdropClick') return;
    onClose?.(event, reason);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionComponent={useTransition ? Transition : undefined}
      fullScreen={fullScreen !== undefined ? fullScreen : isMobile}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      PaperProps={{
        sx: {
          ...((fullScreen !== undefined ? fullScreen : isMobile)
            ? {
              borderRadius: 0,
              height: dialogHeight,
              maxHeight: 'none'
            }
            : {
              borderRadius: '20px',
              maxHeight: '90vh',
              boxShadow: '0 32px 64px rgba(0,0,0,0.12), 0 16px 32px rgba(0,0,0,0.08)',
              border: '1px solid rgba(255,255,255,0.2)',
              backdropFilter: 'blur(20px)',
            }),
          background: '#ffffff',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 'auto',
          ...paperProps.sx
        },
        ...paperProps
      }}
      {...dialogProps}
    >
      {/* En-tête avec dégradé moderne */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #3F51B5 0%, #5C6BC0 50%, #7986CB 100%)',
          py: isMobile ? 3 : 4,
          px: 4,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.3
          },
          ...headerProps.sx
        }}
        {...headerProps}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          position: 'relative', 
          zIndex: 1,
          paddingTop: isMobile ? '20px' : '0px'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {icon && (
              <Box sx={{ mr: 1, color: 'white', display: 'flex', alignItems: 'center' }}>
                {icon}
              </Box>
            )}
            <Box>
              <Typography 
                variant="h5" 
                fontWeight={700}
                sx={{ 
                  color: 'white',
                  mb: subtitle ? 0.5 : 0,
                  letterSpacing: '-0.02em',
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  fontSize: { xs: '1.1rem', sm: '1.1rem' }
                }}
              >
                {title}
              </Typography>
              {subtitle && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'rgba(255,255,255,0.9)',
                    fontWeight: 400,
                    lineHeight: 1.4,
                    fontSize: { xs: '0.6rem', sm: '1rem' }
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>
          
          {showCloseButton && (
            <IconButton 
              onClick={handleClose} 
              size="small"
              sx={{
                background: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(8px)',
                borderRadius: '12px',
                p: 1.5,
                color: 'white',
                '&:hover': {
                  background: 'rgba(255,255,255,0.25)',
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Contenu principal */}
      <DialogContent
        sx={{
          px: customPaddingContent !== null ? customPaddingContent : (isMobile ? 2 : 4),
          py: customPaddingContent !== null ? customPaddingContent : (isMobile ? 2 : 4),
          background: customBackgroundContent,
          overflow: 'auto',
          flex: (fullScreen !== undefined ? fullScreen : isMobile) ? 1 : 'none',
          ...contentProps.sx
        }}
        {...contentProps}
      >
        {children}
      </DialogContent>

      {/* Actions si fournies */}
      {actions && (
        <DialogActions
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            px: isMobile ? 2 : 2,
            py: isMobile ? 2 : 2,
            background: customBackgroundContent,
            borderTop: '1px solid rgba(0,0,0,0.05)',
            '& .MuiButton-root': {
              fontSize: isMobile ? '0.8rem' : '1rem',
              minHeight: '36px',
              py: 1,
              px: 2
            }
          }}
        >
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default BaseDialog; 